import { useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Settings, VolumeX, Flag, Clock, Check, X, Loader2, AlertTriangle, UserX } from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import type { ChatMute, ChatReport } from './hooks/use-chat-moderation';
import type { PersonalMute } from './hooks/use-personal-mutes';

interface ChatModerationPanelProps {
  mutes: ChatMute[];
  reports: ChatReport[];
  mutesLoading: boolean;
  reportsLoading: boolean;
  onUnmute: (oderId: string) => Promise<void>;
  isUnmuting: boolean;
  onResolveReport: (reportId: string, status: 'dismissed' | 'actioned', actionTaken?: string) => Promise<void>;
  isResolvingReport: boolean;
  // Personal mutes props
  personalMutes?: PersonalMute[];
  personalMutesLoading?: boolean;
  onPersonalUnmute?: (userId: string) => Promise<void>;
  isPersonalUnmuting?: boolean;
}

export function ChatModerationPanel({
  mutes,
  reports,
  mutesLoading,
  reportsLoading,
  onUnmute,
  isUnmuting,
  onResolveReport,
  isResolvingReport,
  personalMutes = [],
  personalMutesLoading = false,
  onPersonalUnmute,
  isPersonalUnmuting = false,
}: ChatModerationPanelProps) {
  const [activeTab, setActiveTab] = useState('mutes');
  const activeMutes = mutes.filter((m) => m.isActive);

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Settings className="h-5 w-5" />
          {reports.length > 0 && (
            <span className="absolute -top-1 -right-1 h-4 w-4 bg-destructive text-destructive-foreground text-xs rounded-full flex items-center justify-center">
              {reports.length > 9 ? '9+' : reports.length}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[400px] sm:w-[540px]">
        <SheetHeader>
          <SheetTitle>Chat Moderation</SheetTitle>
          <SheetDescription>
            Manage muted users and review reports for this league's chat.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="mutes" className="flex items-center gap-1 text-xs px-2">
                <VolumeX className="h-3 w-3" />
                League ({activeMutes.length})
              </TabsTrigger>
              <TabsTrigger value="personal" className="flex items-center gap-1 text-xs px-2">
                <UserX className="h-3 w-3" />
                My Mutes ({personalMutes.length})
              </TabsTrigger>
              <TabsTrigger value="reports" className="flex items-center gap-1 text-xs px-2">
                <Flag className="h-3 w-3" />
                Reports
                {reports.length > 0 && (
                  <Badge variant="destructive" className="ml-1 text-xs px-1">
                    {reports.length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            {/* Muted Users Tab */}
            <TabsContent value="mutes" className="mt-4">
              {mutesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : activeMutes.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <VolumeX className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No muted users</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                  {activeMutes.map((mute) => (
                    <MuteCard
                      key={mute.id}
                      mute={mute}
                      onUnmute={() => onUnmute(mute.id)}
                      isUnmuting={isUnmuting}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Personal Mutes Tab */}
            <TabsContent value="personal" className="mt-4">
              {personalMutesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : personalMutes.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <UserX className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No personal mutes</p>
                  <p className="text-xs mt-1">Users you mute here will be hidden across all leagues</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                  {personalMutes.map((mute) => (
                    <PersonalMuteCard
                      key={mute.id}
                      mute={mute}
                      onUnmute={() => onPersonalUnmute?.(mute.mutedId)}
                      isUnmuting={isPersonalUnmuting}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Reports Tab */}
            <TabsContent value="reports" className="mt-4">
              {reportsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : reports.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Flag className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No pending reports</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                  {reports.map((report) => (
                    <ReportCard
                      key={report.id}
                      report={report}
                      onResolve={(status, actionTaken) =>
                        onResolveReport(report.id, status, actionTaken)
                      }
                      isResolving={isResolvingReport}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// Mute Card Component
interface MuteCardProps {
  mute: ChatMute;
  onUnmute: () => void;
  isUnmuting: boolean;
}

function MuteCard({ mute, onUnmute, isUnmuting }: MuteCardProps) {
  const expiryText = mute.expiresAt
    ? `Expires ${formatDistanceToNow(mute.expiresAt, { addSuffix: true })}`
    : 'Permanent';

  return (
    <Card>
      <CardHeader className="py-3 px-4">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-sm font-medium">
              {mute.username || mute.userId}
            </CardTitle>
            <CardDescription className="text-xs flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {expiryText}
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onUnmute}
            disabled={isUnmuting}
          >
            {isUnmuting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              'Unmute'
            )}
          </Button>
        </div>
      </CardHeader>
      {mute.reason && (
        <CardContent className="py-2 px-4 pt-0">
          <p className="text-xs text-muted-foreground">
            Reason: {mute.reason}
          </p>
        </CardContent>
      )}
    </Card>
  );
}

// Personal Mute Card Component
interface PersonalMuteCardProps {
  mute: PersonalMute;
  onUnmute: () => void;
  isUnmuting: boolean;
}

function PersonalMuteCard({ mute, onUnmute, isUnmuting }: PersonalMuteCardProps) {
  return (
    <Card>
      <CardHeader className="py-3 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Avatar className="h-8 w-8">
              <AvatarImage src={mute.avatarImageUrl || undefined} alt={mute.username || 'User'} />
              <AvatarFallback>
                {(mute.username || 'U').charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <CardTitle className="text-sm font-medium">
                {mute.username || 'Unknown User'}
              </CardTitle>
              <CardDescription className="text-xs">
                Muted {formatDistanceToNow(new Date(mute.createdAt), { addSuffix: true })}
              </CardDescription>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onUnmute}
            disabled={isUnmuting}
          >
            {isUnmuting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              'Unmute'
            )}
          </Button>
        </div>
      </CardHeader>
    </Card>
  );
}

// Report Card Component
interface ReportCardProps {
  report: ChatReport;
  onResolve: (status: 'dismissed' | 'actioned', actionTaken?: string) => void;
  isResolving: boolean;
}

function ReportCard({ report, onResolve, isResolving }: ReportCardProps) {
  return (
    <Card>
      <CardHeader className="py-3 px-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-orange-500" />
              {report.category}
            </CardTitle>
            <CardDescription className="text-xs">
              Reported {formatDistanceToNow(report.createdAt, { addSuffix: true })}
            </CardDescription>
          </div>
          <Badge variant="outline" className="text-xs">
            By: {report.reporterUsername || 'User'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="py-2 px-4 pt-0 space-y-3">
        {/* Message preview */}
        <div className="p-2 bg-muted rounded-lg">
          <p className="text-xs text-muted-foreground mb-1">
            From: {report.messageAuthorUsername || report.messageAuthorId}
          </p>
          <p className="text-sm line-clamp-2">{report.messageContent}</p>
        </div>

        {/* Description */}
        {report.description && (
          <p className="text-xs text-muted-foreground">
            Note: {report.description}
          </p>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-1">
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => onResolve('dismissed')}
            disabled={isResolving}
          >
            <X className="h-4 w-4 mr-1" />
            Dismiss
          </Button>
          <Button
            variant="destructive"
            size="sm"
            className="flex-1"
            onClick={() => onResolve('actioned', 'message_deleted')}
            disabled={isResolving}
          >
            <Check className="h-4 w-4 mr-1" />
            Delete & Warn
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
